import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { UploadProvider } from '../contexts/UploadContext';
import { WizardCompletionProvider } from '../contexts/WizardCompletionContext';
import { FieldVisibilityProvider } from '../contexts/FieldVisibilityContext';
import { AutosaveStatusContext } from '../contexts/contexts';
import { useUpload } from '../hooks/useUpload';
import { useCatalog } from '../hooks/useCatalog';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { resolveCatalogAliIdForRequest } from '../utils/resolveCatalogAliIdForRequest';
import { useUploadImageVisibility } from '../hooks/useUploadImageVisibility';
import { useCollections } from '../hooks/useCollections';
import { logger } from '../utils/logger';
import { getProcessingStatus } from '../utils/processingApi';
import { AutosaveStatus } from '../components/AutosaveIndicator';
import { EditModeCompletionInitializer } from '../components/EditModeCompletionInitializer';
import { EDIT_OBJECT_STEPS } from '../components/edit-object/EditObjectSteps';
import { EditObjectShell, Layout } from '../components/edit-object/design/Shell';
import { EditObjectRoadmap } from '../components/edit-object/design/EditObjectRoadmap';
import type { RoadmapSaveStatus } from '../components/edit-object/design/Sidebar';
import {
  computeSidebarSaveStatus,
  derivePrimaryThumbUrl,
  shouldShowSidebarSaveStatus,
} from './edit-object/sidebarHelpers';
import { EditObjectStepShell } from '../components/edit-object/design/StepShell';
import { UploadImagesStep } from './edit-object/UploadImagesStep';
import { DescribeStep } from './edit-object/DescribeStep';
import { GettyStep } from './edit-object/GettyStep';
import { ProvenanceStep } from './edit-object/ProvenanceStep';
import { ExhibitionsStep } from './edit-object/ExhibitionsStep';
import { PublicationsStep } from './edit-object/PublicationsStep';
import { AwardsStep } from './edit-object/AwardsStep';
import { RelatedWorksStep } from './edit-object/RelatedWorksStep';
import { CatalogKeywordsStep } from './edit-object/CatalogKeywordsStep';
import { PrivateOnlyStep } from './edit-object/PrivateOnlyStep';
import { ReviewStep } from './edit-object/ReviewStep';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'edit-object-sidebar-collapsed';

const EditObjectContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Read the page position passed by MyObjects (or ObjectDetails) so we can
  // restore it when the user returns via Finished / item-destroyed / back.
  const incomingBrowsePage = (location.state as { browsePage?: number } | null)?.browsePage;
  // Read the origin marker set by ObjectDetails when launching the editor from
  // the baseball card, so Finished can return to the right place.
  const incomingFrom = (location.state as { browsePage?: number; from?: string } | null)?.from;
  const {
    catalogId,
    addImage,
    setCatalogId,
    uploadedImages,
    selectedCoverId,
    setSelectedCoverId,
    aliCatalogNumber,
    setAliCatalogNumber,
    catalogTitle,
    setCatalogTitle,
  } = useUpload();
  const { getCatalogItem, getKeywords, getDescription, currentItem } = useCatalog();
  const { activeCatalogOwnerId, activeCatalogAliId, switchToOwnCatalog } = useActiveCatalog();
  const { selectCollection } = useCollections();

  const editId = searchParams.get('edit');
  const reuseId = searchParams.get('reuseId');
  const ownerParam = searchParams.get('owner');
  const isEditMode = !!editId;
  const isReuseMode = !!reuseId;
  const effectiveOwnerId = isEditMode
    ? (ownerParam || activeCatalogOwnerId)
    : activeCatalogOwnerId;

  const { toggleVisibility, error: imageVisibilityErrorRaw } =
    useUploadImageVisibility(effectiveOwnerId);
  const imageVisibilityError =
    imageVisibilityErrorRaw == null
      ? null
      : typeof imageVisibilityErrorRaw === 'string'
      ? imageVisibilityErrorRaw
      : (imageVisibilityErrorRaw as Error).message;

  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | undefined>();
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
        } catch {
          // localStorage write failures are non-fatal — state still flips this session.
        }
      }
      return next;
    });
  }, []);
  const [preloadedCatalogItem, setPreloadedCatalogItem] = useState<{
    images?: unknown[];
    coverImageId?: string | null;
    status?: string;
  } | null>(null);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);

  const editDataLoadedRef = useRef(false);
  const total = EDIT_OBJECT_STEPS.length;
  const activeStep = EDIT_OBJECT_STEPS[activeStepIndex];
  const effectiveCatalogId = editId || catalogId || currentItem?.catalogItemId;

  // Top-right "Finish" action shown on every step. Mirrors UploadObject's
  // wizard-finished-button — relies on each section's unmount-flush to
  // persist any pending edits before navigation.
  const handleFinish = useCallback(() => {
    selectCollection(null);
    if (incomingFrom === 'object-details' && editId) {
      navigate(`/object-details/${editId}`);
    } else {
      navigate('/my-objects', { state: { browsePage: incomingBrowsePage } });
    }
  }, [navigate, selectCollection, incomingBrowsePage, incomingFrom, editId]);

  const handleBack = useCallback(() => {
    setActiveStepIndex((idx) => Math.max(0, idx - 1));
  }, []);

  const handleContinue = useCallback(() => {
    setActiveStepIndex((idx) => Math.min(total - 1, idx + 1));
  }, [total]);

  // Reset stale delegate context when editing without an explicit owner param.
  useEffect(() => {
    if (isEditMode && !ownerParam && activeCatalogOwnerId) {
      switchToOwnCatalog();
    }
  }, [isEditMode, ownerParam, activeCatalogOwnerId, switchToOwnCatalog]);

  // Reuse mode: set catalog ID to the cleared slot for new uploads.
  useEffect(() => {
    if (isReuseMode && reuseId) {
      setCatalogId(reuseId);
    }
  }, [isReuseMode, reuseId, setCatalogId]);

  // Reset catalogTitle when entering a new-item flow.
  useEffect(() => {
    if (!isEditMode) {
      setCatalogTitle('');
    }
  }, [isEditMode, setCatalogTitle]);

  // Scroll to top on step change.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeStepIndex]);

  // Load existing catalog data when in edit mode (mirrors UploadObject).
  useEffect(() => {
    if (!isEditMode || !editId || editDataLoadedRef.current) return;
    if (uploadedImages.length > 0) {
      editDataLoadedRef.current = true;
      return;
    }
    editDataLoadedRef.current = true;

    const load = async () => {
      try {
        setCatalogId(editId);
        const item = await getCatalogItem(editId, effectiveOwnerId);
        setPreloadedCatalogItem({
          images: item.images,
          coverImageId: item.coverImageId ?? null,
          status: item.status,
        });

        if (item.images && item.images.length > 0) {
          item.images.forEach((image) => {
            const thumb =
              image.renditions?.find((r) => r.size === 'thumb')?.url ||
              image.renditions?.[0]?.url ||
              image.url ||
              null;
            addImage({
              imageId: image.imageId,
              file: null,
              // GH#1258: FAILED must map to 'failed' (ImageGrid's failed
              // overlay), not 'processing' — see UploadObject.tsx for the
              // same fix and full rationale.
              status: image.status === 'READY' ? 'completed'
                : image.status === 'FAILED' ? 'failed'
                : 'processing',
              thumbnailUrl: thumb,
              visibility: image.visibility || 'private',
              uploadedAt: image.uploadedAt ?? null,
            });
          });
          if (item.coverImageId) setSelectedCoverId(item.coverImageId);

          if (item.aliCatalogNumber) {
            setAliCatalogNumber(item.aliCatalogNumber);
          } else {
            try {
              const k = (await getKeywords(editId, effectiveOwnerId)) as {
                aliCatalogNumber?: string;
              };
              if (k.aliCatalogNumber) setAliCatalogNumber(k.aliCatalogNumber);
            } catch (err) {
              logger.debug('Keywords not found for catalog item', err);
            }
          }

          try {
            const d = (await getDescription(editId, effectiveOwnerId)) as {
              title?: string;
            };
            if (d.title) setCatalogTitle(d.title);
          } catch (err) {
            logger.debug('Description not found for catalog item', err);
          }
        } else if ((item.status as string) === 'CLEARED') {
          logger.debug('[edit-object] CLEARED item, skipping image fallback');
        } else {
          try {
            const status = await getProcessingStatus(editId);
            const thumb =
              status.thumbnailUrl ||
              status.renditions?.find((r) => r.size === 'thumb')?.url ||
              status.renditions?.[0]?.url ||
              null;
            const fallbackImageId = item.coverImageId || editId;
            addImage({
              imageId: fallbackImageId,
              file: null,
              status:
                status.status === 'READY'
                  ? 'completed'
                  : status.status === 'FAILED'
                  ? 'failed'
                  : 'processing',
              thumbnailUrl: thumb,
              visibility: 'private',
            });
          } catch (err) {
            logger.debug('Fallback processing status not available', err);
          }
        }
      } catch (err) {
        logger.error('Failed to load existing catalog data', err);
        const ax = err as { response?: { status?: number } };
        if (ax.response?.status === 404) {
          setEditLoadError(
            'This item could not be found. It may belong to a different catalog.',
          );
        } else {
          setEditLoadError('Failed to load this item. Please try refreshing the page.');
        }
        setPreloadedCatalogItem({ images: [], coverImageId: null });
      }
    };

    void load();
  }, [
    isEditMode,
    editId,
    setCatalogId,
    addImage,
    uploadedImages.length,
    getCatalogItem,
    setSelectedCoverId,
    setAliCatalogNumber,
    getKeywords,
    getDescription,
    setCatalogTitle,
    effectiveOwnerId,
  ]);

  // Render the active step body (without chrome — StepShell wraps it).
  const stepBody = useMemo(() => {
    const sharedProps = {
      catalogItemId: effectiveCatalogId,
      ownerId: effectiveOwnerId,
    };
    switch (activeStep.id) {
      case 'images':
        return (
          <UploadImagesStep
            ownerId={effectiveOwnerId}
            // PR 4d: forward the active own-catalog LL-NXX so upload-initiate
            // mints under that catalog (primary or add-on) instead of falling
            // through to the user's primary. Resolver returns null for
            // delegated contexts — owner_id is the catalog selector there.
            catalogAliId={resolveCatalogAliIdForRequest(activeCatalogAliId, effectiveOwnerId)}
            toggleVisibility={toggleVisibility}
            imageVisibilityError={imageVisibilityError}
          />
        );
      case 'describe':
        return <DescribeStep {...sharedProps} isEditMode={isEditMode} />;
      case 'getty':
        return <GettyStep {...sharedProps} />;
      case 'provenance':
        return (
          <ProvenanceStep
            {...sharedProps}
            itemStatus={preloadedCatalogItem?.status}
          />
        );
      case 'exhibitions':
        return <ExhibitionsStep {...sharedProps} />;
      case 'publications':
        return <PublicationsStep {...sharedProps} />;
      case 'awards':
        return <AwardsStep {...sharedProps} />;
      case 'related':
        return <RelatedWorksStep {...sharedProps} />;
      case 'catalog':
        return <CatalogKeywordsStep {...sharedProps} />;
      case 'additional':
        return <PrivateOnlyStep {...sharedProps} />;
      case 'review':
        return (
          <ReviewStep
            {...sharedProps}
            onNavigateToTab={(tab) => {
              const idx = EDIT_OBJECT_STEPS.findIndex((s) => s.id === tab);
              if (idx >= 0) setActiveStepIndex(idx);
            }}
          />
        );
      default:
        return null;
    }
  }, [
    activeStep.id,
    effectiveCatalogId,
    effectiveOwnerId,
    activeCatalogAliId,
    toggleVisibility,
    imageVisibilityError,
    preloadedCatalogItem?.status,
    isEditMode,
  ]);

  const isFirst = activeStepIndex === 0;
  const isLast = activeStepIndex === total - 1;
  const reviewIndex = total - 1;
  const prevStepLabel = isFirst ? undefined : EDIT_OBJECT_STEPS[activeStepIndex - 1]?.label;
  const nextStepLabel = isLast ? undefined : EDIT_OBJECT_STEPS[activeStepIndex + 1]?.label;
  const showSkip = !['images', 'describe', 'review'].includes(activeStep.id) && !isLast;
  const handleSkipToReview = useCallback(
    () => setActiveStepIndex(reviewIndex),
    [reviewIndex],
  );

  const sidebarSaveStatus: RoadmapSaveStatus | undefined = shouldShowSidebarSaveStatus(
    autosaveStatus,
    lastSaveTime,
  )
    ? computeSidebarSaveStatus(autosaveStatus, lastSaveTime)
    : undefined;

  const primaryThumbUrl = useMemo<string | null>(
    () => derivePrimaryThumbUrl(uploadedImages, selectedCoverId),
    [uploadedImages, selectedCoverId],
  );

  const sidebarArtwork = useMemo(
    () => ({
      title: catalogTitle,
      objectId: aliCatalogNumber || undefined,
      thumbUrl: primaryThumbUrl,
      thumbAlt: catalogTitle || 'Object thumbnail',
    }),
    [catalogTitle, aliCatalogNumber, primaryThumbUrl],
  ) as const;

  return (
    <FieldVisibilityProvider catalogItemId={effectiveCatalogId || ''} ownerId={effectiveOwnerId}>
      <WizardCompletionProvider catalogId={effectiveCatalogId || ''}>
        {editId && (
          <EditModeCompletionInitializer
            editId={editId}
            ownerId={effectiveOwnerId}
            preloadedCatalogItem={preloadedCatalogItem}
          />
        )}
        <AutosaveStatusContext.Provider
          value={{ setStatus: setAutosaveStatus, setLastSaveTime }}
        >
          <EditObjectShell sidebarCollapsed={sidebarCollapsed}>
            {editLoadError ? (
              <div role="alert" style={{ color: 'var(--danger)', padding: '20px' }}>
                {editLoadError}
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => navigate('/my-objects', { state: { browsePage: incomingBrowsePage } })}
                  >
                    Go to My Catalog
                  </button>
                </div>
              </div>
            ) : (
              <Layout>
                <EditObjectRoadmap
                  activeStepIndex={activeStepIndex}
                  onStepChange={setActiveStepIndex}
                  collapsed={sidebarCollapsed}
                  onToggleCollapse={toggleSidebarCollapsed}
                  artwork={sidebarArtwork}
                  saveStatus={sidebarSaveStatus}
                />
                <EditObjectStepShell
                  hideBack={isFirst}
                  prevLabel={prevStepLabel}
                  onBack={isFirst ? undefined : handleBack}
                  showSkip={showSkip}
                  onSkip={handleSkipToReview}
                  nextLabel={nextStepLabel}
                  primaryLabel={isLast ? 'Finished' : undefined}
                  onContinue={isLast ? undefined : handleContinue}
                  hideContinue={isLast}
                  onFinish={handleFinish}
                >
                  {stepBody}
                </EditObjectStepShell>
              </Layout>
            )}
          </EditObjectShell>
        </AutosaveStatusContext.Provider>
      </WizardCompletionProvider>
    </FieldVisibilityProvider>
  );
};

/**
 * Redesigned object editor — 10-step linear wizard that replaces
 * UploadObject during the soak window. Gated by the
 * `enableEditObjectRedesign` feature flag; when off, the route is not
 * registered (see App.tsx). Mirrors UploadObject's provider stack and
 * edit-mode loading so existing wizard tabs work unchanged inside the
 * new shell. Cleanup MR after soak removes UploadObject.
 */
export const EditObject: React.FC = () => (
  <UploadProvider>
    <EditObjectContent />
  </UploadProvider>
);

export default EditObject;
